import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { formatCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { IBANInput } from '@adornis/formfield/fields/iban-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useEffect } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { CardHeader } from '@adornis/components/client/CardHeader.js';
import { addPageHeaderAdditional, removePageHeaderAdditional } from '@martinus/styles/client/PageHeader.js';
import { PageTabs } from '@martinus/styles/client/PageTabs.js';
import { html, nothing } from 'lit';
import { SgAccountingDetails } from '../../../accounting/client/SgAccountingDetails.js';
import { SGBillingPositionEdit } from '../../../billingPositions/client/sg-billing-position-edit.js';
import { BillingPosition } from '../../../billingPositions/entities/BillingPosition.js';
import { SgFormGridEntry } from '../../../client/components/SgFormGridEntry.js';
import { SgSnackbar } from '../../../client/components/ui/sg-snackbar.js';
import { isBillingLocked } from '../../api/groupBillingLock.js';
import { Group } from '../../entities/Group.js';

enum GroupAccountingTab {
  ACCOUNTING_OPTIONS = 'Buchaltungsoptionen',
  ACCOUNTING_DETAILS = 'Details',
}

const groupAccountingSubPages = {
  accountingOptions: {
    label: GroupAccountingTab.ACCOUNTING_OPTIONS,
    path: '/options',
  },
  accountingDetails: {
    label: GroupAccountingTab.ACCOUNTING_DETAILS,
    path: '/details',
  },
} as const;

const SgGroupDetailsAccoutingOptions = stateful((props: { groupId: Maybe<string> }) => {
  const { value: group, loading } = useQuery(
    () =>
      subscribeByID(
        Group,
        props.groupId,
      )({
        iban: 1,
        _id: 1,
        _class: 1,
        parentIDs: 1,
        parents: {
          _id: 1,
          _class: 1,
          parentIDs: 1,
          parents: {
            _id: 1,
            parentIDs: 1,
            _class: 1,
          },
        },
        billingPosition: selectionSet(() => BillingPosition, 1),
        billingRhythmFixed: 1,
        billingLockStichtag: 1,
        billingLockReleaseDay: 1,
      }),
    [props.groupId],
  );

  if (loading || !group)
    return html`
      <div class="flex flex-col grow m-4">
        <div class="card gap-8 p-4">
          <h2 class="card-title">Buchhaltung</h2>
          <div class="grid grid-cols-[1fr_3fr] items-center gap-4">
            <div class="skeleton h-10"></div>
            <div class="skeleton h-10"></div>
            <div class="skeleton h-10"></div>
            <div class="skeleton h-10"></div>
            <div class="skeleton h-10"></div>
            <div class="skeleton h-10"></div>
          </div>
        </div>
      </div>
    `;

  const form = useForm(
    group,
    {
      validator: {
        ...Group.prototype.validator.functions,
        billingPosition: BillingPosition.prototype.validator.functions,
      },
    },
    [group],
  );

  // everything on this tab goes into the export, so a group in billing shows it read only - the server refuses it too
  const locked = isBillingLocked(group, todayAsCalendarDay());

  const handleSave = () => {
    void guardDialog(
      async () => {
        if (form.document._id) {
          await form.document.save();
        }
      },
      {
        success: () => {
          void SgSnackbar.show('Gruppe erfolgreich gespeichert');
        },
      },
    );
  };

  return html`
    ${
      locked
        ? html`<div role="alert" class="alert alert-warning alert-soft m-4 mb-0 text-base-content">
            <span class="icon-[material-symbols--lock-outline-rounded] text-xl"></span>
            <span
              >Die Gruppe ist in der
              Abrechnung${
                group.billingLockReleaseDay
                  ? ` (frei ab ${formatCalendarDay(group.billingLockReleaseDay, 'dd.MM.yyyy')})`
                  : ''
              }.
              IBAN und Abrechnungsposition können erst nach der Freigabe geändert werden.</span
            >
          </div>`
        : nothing
    }
    <!-- a disabled fieldset disables every input inside it -->
    <fieldset class="card-container m-0 min-w-0 border-0 p-0" ?disabled=${locked}>
      <div class="card card-body flex-col">
        ${CardHeader({
          title: 'Buchhaltungsinformationen',
        })}

        <div class="grid grid-cols-[1fr_4fr] items-center gap-4">
          ${SgFormGridEntry({
            label: 'IBAN für Zahlungsverkehr',
            render: html`
              ${IBANInput({
                ...form.field('iban'),
                placeholder: 'Keine IBAN hinterlegt',
                placeholderMode: 'hide',
              })}
            `,
          })}
        </div>
      </div>

      <div class="card card-body flex-col">
        ${CardHeader({
          title: 'Abrechnungsposition',
        })}
        ${SGBillingPositionEdit({ form })}
      </div>
    </fieldset>
    <div
      class="fixed bottom-0 z-20 flex justify-end gap-4 p-4 border-t border-base-300 bg-base-100/80 shadow-[0_-8px_24px_-12px_rgb(16_42_49/0.18)] backdrop-blur-md backdrop-saturate-150"
      ${css({
        width: navigator.userAgent.toLowerCase().includes('firefox') ? '-moz-available' : '-webkit-fill-available',
      })}
    >
      <button class="btn btn-primary btn-lg" ?disabled=${locked} @click=${handleSave}>
        <span class="icon-[material-symbols--save-outline-rounded] size-5"></span>
        Speichern
      </button>
    </div>
  `;
});

export const groupAccountingDetailRouter = makeRouter({
  root: {
    path: '/',
    render: params => SgGroupDetailsAccoutingOptions({ groupId: params.groupId }),
    documentTitlePart: '',
  },
  [groupAccountingSubPages.accountingDetails.label]: {
    path: groupAccountingSubPages.accountingDetails.path,
    render: params => SgAccountingDetails({ referencedEntityID: params.groupId, perspective: 'school' }),
  },
});

export const SgGroupDetailsAccounting = stateful(() => {
  useEffect(() => {
    addPageHeaderAdditional(
      1,
      PageTabs({
        tabs: Object.values(groupAccountingSubPages).map(subPage => ({ key: subPage.label, label: subPage.label })),
        activeKey: () =>
          groupAccountingDetailRouter.currentRoute.value.route.name === 'root'
            ? GroupAccountingTab.ACCOUNTING_OPTIONS
            : groupAccountingDetailRouter.currentRoute.value.route.name,
        onSelect: tabKey => {
          if (tabKey === GroupAccountingTab.ACCOUNTING_OPTIONS) {
            groupAccountingDetailRouter.goTo('root');
          } else {
            groupAccountingDetailRouter.goTo(tabKey);
          }
        },
      }),
    );
    return () => {
      removePageHeaderAdditional(1);
    };
  }, []);

  return groupAccountingDetailRouter.outlet.value;
});
